// PlayFirst Parent — Our Collaborators page.
// /parent-app/collaborators
//
// Three tabs:
//   • Parent Courses   (kind='course')
//   • Services         (kind='service')   — Moms-n-Tots, ECI clinics, OT etc.
//   • Find a Therapist (kind='therapist') — speech therapists open to referrals
//
// Filters: country (dropdown built from the live data), city (free text),
// plus a "Near me" button that asks navigator.geolocation, prefills city by
// reverse-geocoding (best-effort, free public BigDataCloud endpoint), and
// sorts results by haversine distance when collaborators have lat/lng.
//
// Super-admin sees inline Add / Edit / Delete controls; everyone else sees
// a clean read-only browse.
import { useEffect, useMemo, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Heart, ArrowLeft, BookOpen, MapPin, Stethoscope, Plus, ExternalLink,
  Edit, Trash2, Loader2, X, Compass, Mail, Phone, Filter, Sparkles, Search,
  Image as ImageIcon,
} from 'lucide-react';
import { PFP } from './styles';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const TABS = [
  { kind: 'course', label: 'Parent Courses', icon: <BookOpen className="w-4 h-4" />, empty: 'No courses listed yet.' },
  { kind: 'service', label: 'Services', icon: <MapPin className="w-4 h-4" />, empty: 'No services listed yet. Check back soon.' },
  { kind: 'therapist', label: 'Find a Therapist', icon: <Stethoscope className="w-4 h-4" />, empty: 'No therapists listed yet.' },
];

const DELIVERY_MODES = [
  { v: '', l: 'Any delivery mode' },
  { v: 'online', l: 'Online' },
  { v: 'in_person', l: 'In person' },
  { v: 'hybrid', l: 'Hybrid' },
];

// Subtypes for Services tab (Schools / Classes / Parental support / Other).
const SERVICE_TYPES = [
  { v: 'school', l: 'School' },
  { v: 'class', l: 'Class' },
  { v: 'parental_support', l: 'Parental support' },
  { v: 'other', l: 'Other' },
];
const SERVICE_TYPE_LABEL = SERVICE_TYPES.reduce((acc, s) => { acc[s.v] = s.l; return acc; }, {});

function haversineKm(a, b) {
  if (!a || !b) return Infinity;
  const toRad = (d) => (d * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const x = Math.sin(dLat / 2) ** 2 + Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  return 2 * R * Math.asin(Math.sqrt(x));
}

export default function ParentAppCollaborators() {
  const navigate = useNavigate();
  const user = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('user') || 'null'); }
    catch { return null; }
  }, []);
  const isSuperAdmin = !!user?.is_super_admin;
  const auth = useCallback(
    () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }),
    [],
  );

  const [activeTab, setActiveTab] = useState('course');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [countryFilter, setCountryFilter] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [deliveryFilter, setDeliveryFilter] = useState('');
  const [serviceTypeFilter, setServiceTypeFilter] = useState('');
  const [specialDealsOnly, setSpecialDealsOnly] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [myLocation, setMyLocation] = useState(null);  // { lat, lng }
  const [locating, setLocating] = useState(false);

  // Add / edit dialog state (super-admin only)
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [voucherView, setVoucherView] = useState(null);  // {item} when lightbox open
  const [saving, setSaving] = useState(false);
  const emptyForm = {
    kind: 'course', name: '', provider_name: '', description: '',
    external_url: '', contact_email: '', contact_phone: '',
    country: '', city: '', region: '', delivery_mode: '',
    service_type: '',
    age_range: '', tags: '', lat: '', lng: '',
    has_special_offer: false,
    is_neurodiversity_friendly: false,
    nd_accommodations: '',
    image_data: '',
    image_type: '',
    voucher_image_data: '',
    voucher_image_type: '',
  };
  const [form, setForm] = useState(emptyForm);

  // Convert a File to base64 (sans `data:...;base64,` prefix).
  const fileToBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const idx = result.indexOf('base64,');
      resolve(idx >= 0 ? result.slice(idx + 7) : result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const handleVoucherUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Voucher must be an image');
      return;
    }
    if (file.size > 1024 * 1024) {
      toast.error('Voucher image must be under 1 MB');
      return;
    }
    try {
      const b64 = await fileToBase64(file);
      setForm((d) => ({ ...d, voucher_image_data: b64, voucher_image_type: file.type }));
      toast.success('Voucher attached');
    } catch {
      toast.error("Couldn't read voucher image");
    }
  };

  const clearVoucher = () => setForm((d) => ({ ...d, voucher_image_data: '', voucher_image_type: '' }));

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Logo must be an image');
      return;
    }
    if (file.size > 1024 * 1024) {
      toast.error('Logo image must be under 1 MB');
      return;
    }
    try {
      const b64 = await fileToBase64(file);
      setForm((d) => ({ ...d, image_data: b64, image_type: file.type }));
      toast.success('Logo attached');
    } catch {
      toast.error("Couldn't read logo image");
    }
  };
  const clearLogo = () => setForm((d) => ({ ...d, image_data: '', image_type: '' }));

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = { kind: activeTab };
      if (countryFilter) params.country = countryFilter;
      if (cityFilter) params.city = cityFilter;
      const r = await axios.get(`${API}/parent-collaborators`, { ...auth(), params });
      setItems(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      toast.error("Couldn't load this list. Try again in a moment.");
    } finally {
      setLoading(false);
    }
  }, [activeTab, countryFilter, cityFilter, auth]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  // Country options built from the live data so we don't hardcode geography.
  const countryOptions = useMemo(() => {
    const set = new Set(items.map((i) => (i.country || '').trim()).filter(Boolean));
    return Array.from(set).sort();
  }, [items]);

  // Apply client-side filters + sort. Default sort is alphabetical by name;
  // overridden by haversine distance when "Near me" is on.
  const visibleItems = useMemo(() => {
    let list = items;
    if (deliveryFilter) {
      list = list.filter((i) => (i.delivery_mode || '') === deliveryFilter);
    }
    if (activeTab === 'service' && serviceTypeFilter) {
      list = list.filter((i) => (i.service_type || '') === serviceTypeFilter);
    }
    if (specialDealsOnly) {
      list = list.filter((i) => !!i.has_special_offer);
    }
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      list = list.filter((i) => {
        const hay = [
          i.name, i.provider_name, i.description,
          i.city, i.region, i.country,
          ...(Array.isArray(i.tags) ? i.tags : []),
        ].filter(Boolean).join(' ').toLowerCase();
        return hay.includes(q);
      });
    }
    if (myLocation) {
      list = [...list].sort((a, b) => {
        const da = (a.lat != null && a.lng != null) ? haversineKm(myLocation, { lat: a.lat, lng: a.lng }) : Infinity;
        const db = (b.lat != null && b.lng != null) ? haversineKm(myLocation, { lat: b.lat, lng: b.lng }) : Infinity;
        return da - db;
      });
    } else {
      // Default: alphabetical by name (case-insensitive, locale-aware).
      list = [...list].sort((a, b) =>
        (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }),
      );
    }
    return list;
  }, [items, deliveryFilter, activeTab, serviceTypeFilter, specialDealsOnly, searchQuery, myLocation]);

  const handleNearMe = async () => {
    if (!navigator.geolocation) {
      toast.error("Your browser doesn't support location.");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude: lat, longitude: lng } = pos.coords;
        setMyLocation({ lat, lng });
        // Best-effort reverse geocode (no API key required).
        try {
          const r = await fetch(
            `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lng}&localityLanguage=en`,
          );
          if (r.ok) {
            const j = await r.json();
            if (j.countryName && !countryFilter) setCountryFilter(j.countryName);
            if (j.city || j.locality) setCityFilter(j.city || j.locality);
            toast.success(`Showing results near ${j.city || j.locality || j.countryName || 'you'}`);
          } else {
            toast.success('Sorting by distance');
          }
        } catch {
          toast.success('Sorting by distance');
        } finally {
          setLocating(false);
        }
      },
      (err) => {
        setLocating(false);
        toast.error(err.code === 1 ? "Location permission was denied." : "Couldn't get your location.");
      },
      { enableHighAccuracy: false, timeout: 8000 },
    );
  };

  const clearFilters = () => {
    setCountryFilter('');
    setCityFilter('');
    setDeliveryFilter('');
    setServiceTypeFilter('');
    setSpecialDealsOnly(false);
    setSearchQuery('');
    setMyLocation(null);
  };

  const openAdd = () => {
    setEditing(null);
    setForm({ ...emptyForm, kind: activeTab });
    setShowDialog(true);
  };
  const openEdit = (item) => {
    setEditing(item);
    setForm({
      ...emptyForm,
      ...item,
      tags: (item.tags || []).join(', '),
      lat: item.lat == null ? '' : String(item.lat),
      lng: item.lng == null ? '' : String(item.lng),
    });
    setShowDialog(true);
  };

  const submitForm = async (e) => {
    e?.preventDefault();
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }
    const payload = {
      ...form,
      tags: form.tags
        ? form.tags.split(',').map((t) => t.trim()).filter(Boolean)
        : [],
      lat: form.lat === '' ? null : parseFloat(form.lat),
      lng: form.lng === '' ? null : parseFloat(form.lng),
    };
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/parent-collaborators/${editing.id}`, payload, auth());
        toast.success('Updated');
      } else {
        await axios.post(`${API}/parent-collaborators`, payload, auth());
        toast.success('Added');
      }
      setShowDialog(false);
      setEditing(null);
      fetchItems();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.name}"?`)) return;
    try {
      await axios.delete(`${API}/parent-collaborators/${item.id}`, auth());
      toast.success('Deleted');
      fetchItems();
    } catch {
      toast.error('Delete failed');
    }
  };

  const handleVisit = async (item) => {
    if (!item.external_url) return;
    // fire-and-forget click count
    axios.post(`${API}/parent-collaborators/${item.id}/click`, null, auth()).catch(() => {});
    window.open(item.external_url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className={PFP.page}>
      <header className="px-4 sm:px-8 py-4 flex items-center justify-between max-w-6xl mx-auto">
        <button
          onClick={() => navigate('/parent-app/home')}
          className="flex items-center gap-2 text-[#7C6F9C] hover:underline"
          data-testid="collab-back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Home
        </button>
        <Link to="/parent-app/home" className="flex items-center gap-2">
          <Heart className="w-5 h-5 text-[#7C6F9C] fill-[#7C6F9C]/20" />
          <span className="font-serif text-lg text-[#3a2f4f]">PlayFirst</span>
          <span className="text-[10px] uppercase tracking-wider text-[#7C6F9C] font-medium ml-1">
            Parent
          </span>
        </Link>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-8 pb-16">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <h1 className={`${PFP.h1} mb-2`}>Our Collaborators</h1>
          <p className="text-base text-stone-600 max-w-2xl">
            People and places we love working with. Parent courses, in-person services, and
            speech therapists open to early-intervention referrals — all in one place. 💛
          </p>
        </motion.div>

        {/* Exclusive-deals banner */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="mt-6 rounded-2xl bg-amber-50 border border-amber-200 px-4 py-3 flex items-start gap-3"
          data-testid="collab-exclusive-deals-banner"
        >
          <div className="w-9 h-9 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-4 h-4" />
          </div>
          <div className="text-sm text-amber-900 leading-relaxed">
            <strong>Look for the &quot;Special deal inside&quot; sticker.</strong>{' '}
            Many of our collaborators offer exclusive discounts, freebies or first-session
            perks to PlayFirst Parent subscribers. Tap any card with the sticker to see the offer.
          </div>
        </motion.div>

        {/* Tabs */}
        <div className="mt-8 flex flex-wrap gap-2" data-testid="collab-tabs">
          {TABS.map((t) => {
            const active = t.kind === activeTab;
            return (
              <button
                key={t.kind}
                onClick={() => { setActiveTab(t.kind); }}
                data-testid={`collab-tab-${t.kind}`}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  active
                    ? 'bg-[#7C6F9C] text-white shadow-sm'
                    : 'bg-white/80 text-[#5b4f7a] border border-stone-200 hover:border-[#7C6F9C]/40'
                }`}
              >
                {t.icon}
                {t.label}
              </button>
            );
          })}
          {isSuperAdmin && (
            <button
              onClick={openAdd}
              data-testid="collab-add-btn"
              className="ml-auto inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#3a2f4f] text-white text-sm font-medium hover:opacity-90"
            >
              <Plus className="w-4 h-4" /> Add collaborator
            </button>
          )}
        </div>

        {/* Filters */}
        <div className={`${PFP.card} p-4 mt-4 space-y-3`} data-testid="collab-filters">
          {/* Search bar */}
          <div className="relative">
            <Search className="w-4 h-4 text-stone-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <Input
              placeholder="Search by name, provider, description or tag…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-10 pl-9 rounded-full"
              data-testid="collab-search-input"
            />
          </div>

          {/* Filter chips row */}
          <div className="flex flex-wrap items-center gap-3">
            <Filter className="w-4 h-4 text-stone-400" />
            <Select value={countryFilter || 'all'} onValueChange={(v) => setCountryFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="h-10 w-44 rounded-full" data-testid="collab-country-filter">
                <SelectValue placeholder="Country" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All countries</SelectItem>
                {countryOptions.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              placeholder="City contains…"
              value={cityFilter}
              onChange={(e) => setCityFilter(e.target.value)}
              className="h-10 w-44 rounded-full"
              data-testid="collab-city-filter"
            />
            <Select value={deliveryFilter || 'all'} onValueChange={(v) => setDeliveryFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="h-10 w-44 rounded-full" data-testid="collab-mode-filter">
                <SelectValue placeholder="Delivery mode" />
              </SelectTrigger>
              <SelectContent>
                {DELIVERY_MODES.map((m) => (
                  <SelectItem key={m.v || 'all'} value={m.v || 'all'}>{m.l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {activeTab === 'service' && (
              <Select value={serviceTypeFilter || 'all'} onValueChange={(v) => setServiceTypeFilter(v === 'all' ? '' : v)}>
                <SelectTrigger className="h-10 w-44 rounded-full" data-testid="collab-service-type-filter">
                  <SelectValue placeholder="Service type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All service types</SelectItem>
                  {SERVICE_TYPES.map((s) => (
                    <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <button
              onClick={() => setSpecialDealsOnly((v) => !v)}
              data-testid="collab-special-deals-toggle"
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors border ${
                specialDealsOnly
                  ? 'bg-amber-100 text-amber-800 border-amber-300'
                  : 'bg-white text-stone-600 border-stone-200 hover:border-amber-300'
              }`}
            >
              <Sparkles className="w-4 h-4" />
              {specialDealsOnly ? 'Showing deals only' : 'Special deals only'}
            </button>
            <button
              onClick={handleNearMe}
              disabled={locating}
              data-testid="collab-near-me-btn"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#7C6F9C]/10 text-[#7C6F9C] text-sm hover:bg-[#7C6F9C]/15"
            >
              {locating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Compass className="w-4 h-4" />}
              {myLocation ? 'Sorted by distance' : 'Near me'}
            </button>
            {(countryFilter || cityFilter || deliveryFilter || serviceTypeFilter || specialDealsOnly || searchQuery || myLocation) && (
              <button
                onClick={clearFilters}
                className="text-xs text-stone-500 hover:underline"
                data-testid="collab-clear-filters"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Results */}
        <div className="mt-6">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-stone-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
            </div>
          ) : visibleItems.length === 0 ? (
            <div className={`${PFP.card} p-10 text-center`} data-testid="collab-empty-state">
              <p className="text-stone-600">
                {TABS.find((t) => t.kind === activeTab)?.empty || 'Nothing here yet.'}
              </p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {visibleItems.map((item) => (
                <CollaboratorCard
                  key={item.id}
                  item={item}
                  isSuperAdmin={isSuperAdmin}
                  myLocation={myLocation}
                  onVisit={() => handleVisit(item)}
                  onEdit={() => openEdit(item)}
                  onDelete={() => handleDelete(item)}
                  onVoucherClick={(it) => setVoucherView(it)}
                />
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Add / Edit dialog (super-admin) */}
      <Dialog open={showDialog} onOpenChange={(open) => { if (!open) { setEditing(null); setShowDialog(false); } }}>
        <DialogContent className="rounded-3xl max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="collab-dialog">          <DialogHeader>
            <DialogTitle>{editing ? 'Edit collaborator' : 'Add a collaborator'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitForm} className="space-y-3 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Type</Label>
                <Select value={form.kind} onValueChange={(v) => setForm((d) => ({ ...d, kind: v }))}>
                  <SelectTrigger data-testid="collab-form-kind"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="course">Parent Course</SelectItem>
                    <SelectItem value="service">Service</SelectItem>
                    <SelectItem value="therapist">Speech Therapist</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Delivery mode</Label>
                <Select value={form.delivery_mode || ''} onValueChange={(v) => setForm((d) => ({ ...d, delivery_mode: v }))}>
                  <SelectTrigger data-testid="collab-form-delivery"><SelectValue placeholder="Choose…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="online">Online</SelectItem>
                    <SelectItem value="in_person">In person</SelectItem>
                    <SelectItem value="hybrid">Hybrid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {form.kind === 'service' && (
              <div>
                <Label className="text-xs">Service type</Label>
                <Select value={form.service_type || ''} onValueChange={(v) => setForm((d) => ({ ...d, service_type: v }))}>
                  <SelectTrigger data-testid="collab-form-service-type"><SelectValue placeholder="Choose…" /></SelectTrigger>
                  <SelectContent>
                    {SERVICE_TYPES.map((s) => (
                      <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="text-xs">Name *</Label>
              <Input value={form.name} onChange={(e) => setForm((d) => ({ ...d, name: e.target.value }))} required data-testid="collab-form-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Provider / clinic / practice</Label>
                <Input value={form.provider_name} onChange={(e) => setForm((d) => ({ ...d, provider_name: e.target.value }))} data-testid="collab-form-provider" />
              </div>
              <div>
                <Label className="text-xs">Age range</Label>
                <Input value={form.age_range} placeholder="0-3 years" onChange={(e) => setForm((d) => ({ ...d, age_range: e.target.value }))} data-testid="collab-form-age-range" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Description</Label>
              <Textarea rows={3} value={form.description} onChange={(e) => setForm((d) => ({ ...d, description: e.target.value }))} data-testid="collab-form-description" />
            </div>
            <div>
              <Label className="text-xs">External link</Label>
              <Input value={form.external_url} placeholder="https://…" onChange={(e) => setForm((d) => ({ ...d, external_url: e.target.value }))} data-testid="collab-form-url" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Contact email</Label>
                <Input type="email" value={form.contact_email} onChange={(e) => setForm((d) => ({ ...d, contact_email: e.target.value }))} data-testid="collab-form-contact-email" />
              </div>
              <div>
                <Label className="text-xs">Contact phone</Label>
                <Input value={form.contact_phone} onChange={(e) => setForm((d) => ({ ...d, contact_phone: e.target.value }))} data-testid="collab-form-contact-phone" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Country</Label>
                <Input value={form.country} onChange={(e) => setForm((d) => ({ ...d, country: e.target.value }))} data-testid="collab-form-country" />
              </div>
              <div>
                <Label className="text-xs">City</Label>
                <Input value={form.city} onChange={(e) => setForm((d) => ({ ...d, city: e.target.value }))} data-testid="collab-form-city" />
              </div>
              <div>
                <Label className="text-xs">Region (state/province)</Label>
                <Input value={form.region} onChange={(e) => setForm((d) => ({ ...d, region: e.target.value }))} data-testid="collab-form-region" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Latitude (optional)</Label>
                <Input type="number" step="any" value={form.lat} onChange={(e) => setForm((d) => ({ ...d, lat: e.target.value }))} data-testid="collab-form-lat" />
              </div>
              <div>
                <Label className="text-xs">Longitude (optional)</Label>
                <Input type="number" step="any" value={form.lng} onChange={(e) => setForm((d) => ({ ...d, lng: e.target.value }))} data-testid="collab-form-lng" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Tags (comma-separated)</Label>
              <Input value={form.tags} placeholder="GLP, ECI, AAC" onChange={(e) => setForm((d) => ({ ...d, tags: e.target.value }))} data-testid="collab-form-tags" />
            </div>
            <label
              className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                form.has_special_offer
                  ? 'border-amber-300 bg-amber-50/70'
                  : 'border-stone-200 hover:border-stone-300'
              }`}
              data-testid="collab-form-special-offer-label"
            >
              <input
                type="checkbox"
                checked={!!form.has_special_offer}
                onChange={(e) => setForm((d) => ({ ...d, has_special_offer: e.target.checked }))}
                className="mt-0.5"
                data-testid="collab-form-special-offer"
              />
              <span className="text-sm text-stone-700 leading-relaxed">
                <strong>Show &quot;Special deal inside&quot; sticker</strong>
                <span className="block text-xs text-stone-500 mt-0.5">
                  Use this when the partner has offered PlayFirst parents a discount, freebie or
                  exclusive perk. Add the offer details in the Description field above.
                </span>
              </span>
            </label>
            <label
              className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                form.is_neurodiversity_friendly
                  ? 'border-emerald-300 bg-emerald-50/70'
                  : 'border-stone-200 hover:border-stone-300'
              }`}
              data-testid="collab-form-nd-label"
            >
              <input
                type="checkbox"
                checked={!!form.is_neurodiversity_friendly}
                onChange={(e) => setForm((d) => ({ ...d, is_neurodiversity_friendly: e.target.checked }))}
                className="mt-0.5"
                data-testid="collab-form-nd-toggle"
              />
              <span className="text-sm text-stone-700 leading-relaxed">
                <strong>Show &quot;Neurodiversity-friendly&quot; banner</strong>
                <span className="block text-xs text-stone-500 mt-0.5">
                  Indicates this partner welcomes and is experienced with neurodivergent
                  clients. Appears as a green badge on the card — stacks with the special-offer
                  sticker.
                </span>
              </span>
            </label>

            {/* Neurodiversity-accommodations detail — shown as a callout on
                the card when ticked as ND-friendly. Free text so admins can
                describe exactly HOW (sensory rooms, staff training, etc.). */}
            {form.is_neurodiversity_friendly && (
              <div className="space-y-1.5" data-testid="collab-form-nd-accom-section">
                <Label className="text-xs" htmlFor="nd-accommodations">
                  How is this place neurodiversity-accommodating? <span className="text-stone-400 font-normal">(optional but recommended)</span>
                </Label>
                <textarea
                  id="nd-accommodations"
                  rows={4}
                  value={form.nd_accommodations || ''}
                  onChange={(e) => setForm((d) => ({ ...d, nd_accommodations: e.target.value }))}
                  placeholder={
                    "e.g. Sensory-friendly quiet room · staff trained in gestalt language processing · flexible booking · low-lighting session option · AAC-accepting"
                  }
                  className="w-full text-sm p-3 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-emerald-300 bg-white"
                  data-testid="collab-form-nd-accommodations-input"
                />
                <p className="text-xs text-stone-500">
                  Shown as a green &quot;How they welcome neurodivergent clients&quot; callout on
                  the parent-facing card. Be specific — parents choose based on this.
                </p>
              </div>
            )}

            {/* Logo / hero image upload — appears at the top of the
                collaborator card. For courses this is the course cover. */}
            <div className="space-y-2" data-testid="collab-form-logo-section">
              <Label className="text-xs">
                {form.kind === 'course' ? 'Course cover image' : 'Company logo / hero image'} (optional)
              </Label>
              {form.image_data ? (
                <div className="flex items-start gap-3 p-3 rounded-xl border border-stone-200 bg-stone-50">
                  <img
                    src={`data:${form.image_type || 'image/png'};base64,${form.image_data}`}
                    alt="Logo preview"
                    className="w-32 h-32 object-contain rounded-lg border border-stone-200 bg-white flex-shrink-0"
                    data-testid="collab-form-logo-preview"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-stone-700">Logo attached</p>
                    <p className="text-xs text-stone-500 mt-0.5">
                      Shows at the top of the collaborator card. Tap "Remove" to swap it.
                    </p>
                    <button
                      type="button"
                      onClick={clearLogo}
                      className="mt-2 text-xs text-rose-600 hover:underline"
                      data-testid="collab-form-logo-remove"
                    >
                      Remove logo
                    </button>
                  </div>
                </div>
              ) : (
                <label
                  className="flex items-center justify-center gap-2 px-4 py-6 rounded-xl border-2 border-dashed border-stone-200 hover:border-[#7C6F9C] hover:bg-[#7C6F9C]/5 cursor-pointer transition-colors"
                  data-testid="collab-form-logo-upload-label"
                >
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                    data-testid="collab-form-logo-input"
                  />
                  <ImageIcon className="w-5 h-5 text-[#7C6F9C]" />
                  <div className="text-center">
                    <p className="text-sm text-[#7C6F9C] font-medium">
                      {form.kind === 'course' ? 'Upload course cover image' : 'Upload company logo'}
                    </p>
                    <p className="text-xs text-stone-500 mt-0.5">JPG, PNG, SVG · up to 1 MB</p>
                  </div>
                </label>
              )}
            </div>

            {/* Voucher image upload — optional. Shown on the parent's
                collaborator card so they can screenshot/redeem at the
                provider. */}
            <div className="space-y-2" data-testid="collab-form-voucher-section">
              <Label className="text-xs">Voucher / promo image (optional)</Label>
              {form.voucher_image_data ? (
                <div className="flex items-start gap-3 p-3 rounded-xl border border-stone-200 bg-stone-50">
                  <img
                    src={`data:${form.voucher_image_type || 'image/png'};base64,${form.voucher_image_data}`}
                    alt="Voucher preview"
                    className="w-32 h-32 object-cover rounded-lg border border-stone-200 flex-shrink-0"
                    data-testid="collab-form-voucher-preview"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-stone-700">Voucher attached</p>
                    <p className="text-xs text-stone-500 mt-0.5">
                      Parents will see this voucher on the collaborator card. Tap "Remove" to swap it.
                    </p>
                    <button
                      type="button"
                      onClick={clearVoucher}
                      className="mt-2 text-xs text-rose-600 hover:underline"
                      data-testid="collab-form-voucher-remove"
                    >
                      Remove voucher
                    </button>
                  </div>
                </div>
              ) : (
                <label
                  className="flex items-center justify-center gap-2 px-4 py-6 rounded-xl border-2 border-dashed border-stone-200 hover:border-[#7C6F9C] hover:bg-[#7C6F9C]/5 cursor-pointer transition-colors"
                  data-testid="collab-form-voucher-upload-label"
                >
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleVoucherUpload}
                    className="hidden"
                    data-testid="collab-form-voucher-input"
                  />
                  <div className="text-center">
                    <p className="text-sm text-[#7C6F9C] font-medium">Upload voucher image</p>
                    <p className="text-xs text-stone-500 mt-0.5">JPG, PNG · up to 1 MB</p>
                  </div>
                </label>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => { setShowDialog(false); setEditing(null); }} className={PFP.btnGhost}>
                Cancel
              </button>
              <button type="submit" disabled={saving} className={PFP.btnPrimary + ' inline-flex items-center gap-2'} data-testid="collab-form-save">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {editing ? 'Save changes' : 'Add collaborator'}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Voucher lightbox — full-screen image so parents can screenshot or
          show it at checkout. */}
      <Dialog open={!!voucherView} onOpenChange={(o) => { if (!o) setVoucherView(null); }}>
        <DialogContent className="rounded-3xl max-w-lg p-0 overflow-hidden" data-testid="voucher-lightbox">
          {voucherView && (
            <div className="bg-gradient-to-b from-amber-50 to-white">
              <div className="px-5 pt-5 pb-2">
                <p className="text-[11px] uppercase tracking-wider text-amber-700 font-semibold">Voucher</p>
                <h3 className="font-serif text-lg text-[#3a2f4f]">{voucherView.name}</h3>
                {voucherView.provider_name && (
                  <p className="text-sm text-stone-600">{voucherView.provider_name}</p>
                )}
              </div>
              <img
                src={`data:${voucherView.voucher_image_type || 'image/png'};base64,${voucherView.voucher_image_data}`}
                alt={`${voucherView.name} voucher`}
                className="w-full max-h-[70vh] object-contain bg-white"
                data-testid="voucher-lightbox-image"
              />
              <div className="px-5 py-4 text-xs text-stone-500 text-center">
                Screenshot this voucher and show it at the provider to redeem.
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CollaboratorCard({ item, isSuperAdmin, myLocation, onVisit, onEdit, onDelete, onVoucherClick }) {
  const distance = (myLocation && item.lat != null && item.lng != null)
    ? haversineKm(myLocation, { lat: item.lat, lng: item.lng })
    : null;

  // Descriptions are clipped to 4 lines by default so cards stay scannable.
  // Parents (and admins) can tap to expand the whole thing — critical for
  // course listings and neurodiversity-friendly detail that can run long.
  // Only surface the toggle when the description is actually long enough
  // that clipping matters (rough heuristic: ≥ 220 chars OR ≥ 4 newlines).
  const [descExpanded, setDescExpanded] = useState(false);
  const descIsLong = !!item.description && (
    item.description.length > 220 ||
    (item.description.match(/\n/g) || []).length >= 4
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className={`${PFP.card} ${PFP.cardHover} p-5 flex flex-col relative`}
      data-testid={`collab-card-${item.id}`}
    >
      {/* Course cover image — full-width hero at the top of course cards */}
      {item.kind === 'course' && item.image_data && (
        <div className="-mt-5 -mx-5 mb-4 rounded-t-3xl overflow-hidden">
          <img
            src={`data:${item.image_type || 'image/png'};base64,${item.image_data}`}
            alt={`${item.name} cover`}
            className="w-full h-40 object-cover"
            data-testid={`collab-card-course-cover-${item.id}`}
          />
        </div>
      )}
      {/* Stickers — render inline as a top row so they never overlap the
          company name or logo. Wrap on narrow screens so both stay visible. */}
      {(item.has_special_offer || item.is_neurodiversity_friendly) && (
        <div className="flex flex-wrap items-center gap-1.5 mb-2.5" data-testid={`collab-card-badges-${item.id}`}>
          {item.has_special_offer && (
            <span
              className="inline-flex items-center gap-1.5 bg-gradient-to-br from-amber-400 to-amber-500 text-amber-950 text-[11px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full border border-amber-300"
              data-testid={`collab-card-special-offer-${item.id}`}
            >
              <Sparkles className="w-3 h-3" /> Special deal inside
            </span>
          )}
          {item.is_neurodiversity_friendly && (
            <span
              className="inline-flex items-center gap-1.5 bg-gradient-to-br from-emerald-400 to-emerald-500 text-emerald-950 text-[11px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full border border-emerald-300"
              data-testid={`collab-card-nd-friendly-${item.id}`}
              title="This partner welcomes and is experienced with neurodivergent clients"
            >
              <Heart className="w-3 h-3" /> Neurodiversity-friendly
            </span>
          )}
        </div>
      )}
      <div className="flex-1">
        <p className="text-[11px] uppercase tracking-wider text-[#7C6F9C] font-semibold mb-1">
          {item.kind === 'course' ? 'Parent Course' : item.kind === 'service' ? 'Service' : 'Speech Therapist'}
        </p>
        <div className="flex items-center gap-2.5 mb-1">
          {/* Small circular logo only for non-course kinds — courses use the hero above */}
          {item.kind !== 'course' && item.image_data && (
            <img
              src={`data:${item.image_type || 'image/png'};base64,${item.image_data}`}
              alt={`${item.name} logo`}
              className="w-9 h-9 rounded-full object-cover border border-stone-200 flex-shrink-0 bg-white"
              data-testid={`collab-card-logo-${item.id}`}
            />
          )}
          <h3 className="font-serif text-lg text-[#3a2f4f] leading-tight">{item.name}</h3>
        </div>
        {item.provider_name && (
          <p className="text-sm text-stone-600 mb-2">{item.provider_name}</p>
        )}
        {item.description && (
          <div className="mb-3">
            <p
              className={`text-sm text-stone-600 leading-relaxed whitespace-pre-wrap ${descExpanded ? '' : 'line-clamp-4'}`}
              data-testid={`collab-card-description-${item.id}`}
            >
              {item.description}
            </p>
            {descIsLong && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setDescExpanded((v) => !v); }}
                className="mt-1 text-xs font-medium text-[#7C6F9C] hover:text-[#5f5378] underline underline-offset-2"
                data-testid={`collab-card-description-toggle-${item.id}`}
                aria-expanded={descExpanded}
              >
                {descExpanded ? 'Show less' : 'Read more'}
              </button>
            )}
          </div>
        )}
        {/* Neurodiversity-accommodations callout — the "how" behind the badge */}
        {item.is_neurodiversity_friendly && item.nd_accommodations && (
          <div
            className="mb-3 rounded-2xl border border-emerald-200 bg-emerald-50/70 p-3"
            data-testid={`collab-card-nd-accommodations-${item.id}`}
          >
            <p className="text-[10px] uppercase tracking-wider text-emerald-800 font-semibold mb-1 flex items-center gap-1">
              <Heart className="w-3 h-3" /> How they welcome neurodivergent clients
            </p>
            <p className="text-xs text-emerald-950 leading-relaxed whitespace-pre-wrap">
              {item.nd_accommodations}
            </p>
          </div>
        )}
        {item.voucher_image_data && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onVoucherClick && onVoucherClick(item); }}
            className="inline-flex items-center gap-2 mb-3 rounded-full border border-amber-300 bg-amber-50 px-3 py-1.5 hover:bg-amber-100 transition-colors"
            data-testid={`collab-card-voucher-${item.id}`}
            title="Tap to view full voucher"
          >
            <Sparkles className="w-3.5 h-3.5 text-amber-700" />
            <span className="text-xs text-amber-800 font-medium">View voucher</span>
          </button>
        )}
        <div className="flex flex-wrap gap-2 text-xs text-stone-500 mb-3">
          {(item.city || item.country) && (
            <span className="inline-flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              {[item.city, item.region, item.country].filter(Boolean).join(', ')}
            </span>
          )}
          {item.delivery_mode && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-stone-100 rounded-full">
              {item.delivery_mode.replace('_', ' ')}
            </span>
          )}
          {item.kind === 'service' && item.service_type && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#7C6F9C]/10 text-[#5b4f7a] rounded-full">
              {SERVICE_TYPE_LABEL[item.service_type] || item.service_type}
            </span>
          )}
          {item.age_range && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-stone-100 rounded-full">
              {item.age_range}
            </span>
          )}
          {distance != null && Number.isFinite(distance) && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#7C6F9C]/10 text-[#7C6F9C] rounded-full">
              ~{distance < 10 ? distance.toFixed(1) : Math.round(distance)} km away
            </span>
          )}
        </div>
        {(item.tags || []).length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {item.tags.map((t) => (
              <span key={t} className="px-2 py-0.5 text-[11px] rounded-full bg-rose-50 text-rose-700 border border-rose-100">
                {t}
              </span>
            ))}
          </div>
        )}
        {(item.contact_email || item.contact_phone) && (
          <div className="text-xs text-stone-500 space-y-0.5 mb-3">
            {item.contact_email && (
              <p className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{item.contact_email}</p>
            )}
            {item.contact_phone && (
              <p className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{item.contact_phone}</p>
            )}
          </div>
        )}
      </div>
      <div className="flex items-center justify-between gap-2 mt-2">
        {item.external_url ? (
          <button
            onClick={onVisit}
            data-testid={`collab-visit-${item.id}`}
            className="inline-flex items-center gap-1 text-sm font-medium text-[#7C6F9C] hover:underline"
          >
            Visit <ExternalLink className="w-3.5 h-3.5" />
          </button>
        ) : <span />}
        {isSuperAdmin && (
          <div className="flex gap-1">
            <button onClick={onEdit} data-testid={`collab-edit-${item.id}`} className="p-1.5 rounded-full text-stone-500 hover:text-[#7C6F9C] hover:bg-[#7C6F9C]/10">
              <Edit className="w-4 h-4" />
            </button>
            <button onClick={onDelete} data-testid={`collab-delete-${item.id}`} className="p-1.5 rounded-full text-stone-500 hover:text-rose-700 hover:bg-rose-50">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </motion.div>
  );
}
